import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Store, Select } from '@ngxs/store'
import { environment } from 'src/environments/environment'
import { ParticipantState } from '../store/state/participant.state'
import { StudentAdmissionsState } from '../store/state/student-admissions.state'
import { filter, tap } from 'rxjs/operators'
import { GetStudentAuditDocuments } from '../store/actions/student-audit-documents.actions'
import { Observable } from 'rxjs'
import { StudentAudit } from '../models/student-audit'
import { GetStudentAudits } from '../store/actions/student-audits.actions'
import { StudentAuditsState } from 'src/app/store/state/student-audits.state'
import { taskType } from '../models/student-audit'
import { TranslateService } from '@ngx-translate/core'
@Injectable({
  providedIn: 'root',
})
export class FormDataUploadService {
  private headers: HttpHeaders = new HttpHeaders({
    apiKey: environment.apiKey,
  })
  selectedAudit: StudentAudit
  selectedGuid: string
  hasValidationError: boolean
  hasUploadedSuccesfully: boolean
  isDispatching: boolean
  isDownloading: boolean
  validationErrors: string[] = []

  @Select(StudentAdmissionsState.selectedGuid)
  selectedGuid$: Observable<string>
  @Select(StudentAuditsState.selectedAudit)
  selectedAudit$: Observable<StudentAudit>

  constructor(
    private store: Store,
    private http: HttpClient,
    private uploadState: Store,
    private translate: TranslateService
  ) {
    this.selectedGuid$
      .pipe(
        filter((newGuid) => !!newGuid),
        tap((newGuid) => {
          this.selectedGuid = newGuid
        })
      )
      .subscribe()
    this.selectedAudit$
      .pipe(
        filter((stream) => !!stream),
        tap((stream) => {
          if (this.selectedAudit?.id !== stream?.id) {
            this.selectedAudit = stream
            this.isDispatching = false
            this.hasValidationError = false
          }
        })
      )
      .subscribe()
  }

  async uploadBlobAsFile(
    blob,
    fileName,
    alternativeAudit = undefined,
    onSuccess = undefined
  ) {
    this.handleSelectFile(blob, alternativeAudit, fileName, onSuccess)
  }
  
  async uploadDataAsFile(
    data,
    alternativeAudit = undefined,
    fileName = undefined,
    onSuccess = undefined
  ) {
    const blob = new Blob([data], { type: 'text/csv' })
    this.handleSelectFile(blob, alternativeAudit, fileName, onSuccess)
  }

  async handleSelectFile(
    file,
    alternativeAudit = undefined,
    fileName = undefined,
    onSuccess = undefined
  ) {
    const formData = new FormData()
    formData.append(
      'admissionNodeID',
      (alternativeAudit || this.selectedAudit).id
    )
    formData.append('admissionGuid', this.selectedGuid)
    formData.append('myFile', file, fileName)
    this.validateSelectedFile(formData)
    if (!this.hasValidationError) {
      this.isDispatching = true
      await this.sendFormData(formData, onSuccess)
      window.setTimeout(() => {
        this.store.dispatch(new GetStudentAudits())
      }, 2000)
    }
  }

  validateSelectedFile(formData: FormData) {
    this.hasValidationError = false
    this.validationErrors = []
    const file: File = formData.get('myFile') as File
    console.log("validate file size", file.size / 1024 / 1024 )
    if (file.size / 1024 / 1024 > 10) {
      this.hasValidationError = true
      this.setErrorMessage('file-upload.file-size-error')
    }
    const extension: string = file.name.split('.').pop().toLocaleLowerCase()
    if (!this.allowedFileExtensions().includes(extension)) {
      this.hasValidationError = true
      this.setErrorMessage(
        'file-upload.file-type-error',
        this.allowedFileExtensions().join(', ')
      )
    }
    if (file.name.length > 128) {
      this.hasValidationError = true
      this.setErrorMessage(
        'file-upload.file-name-length-error',
        this.allowedFileExtensions().join(', ')
      )
    }
    const regex = /^[a-zA-Z0-9\-_.()\s]+$/;
    if (!regex.test(file.name)) {
      this.hasValidationError = true
      this.setErrorMessage(
        'file-upload.file-name-character-not-allowed'
      )
    }
  }

  async sendFormData(formData: FormData, onSuccess = undefined): Promise<void> {
    const participantId =
      this.store.selectSnapshot(ParticipantState).participantId
    const url = this.constructUrl(
      participantId,
      this.selectedGuid,
      formData.get('admissionNodeID')
    )
    this.http.post<any>(url, formData, { headers: this.headers }).subscribe({
      next: () => {
        this.isDispatching = false
        this.hasValidationError = false
        this.hasUploadedSuccesfully = true
        this.store.dispatch(new GetStudentAuditDocuments())
        if (onSuccess && (typeof onSuccess == "function")) {
          onSuccess();   
        }
      },
      error: (e: any) => {
        console.warn('Error uploading: ', e)
        const error: string = e.error.Message || e.message || e
        if (error.includes('Maximum request length exceeded.')) {
          this.setErrorMessage('file-upload.file-size-error')
        } else if (error.includes('wrong extension')) {
          this.setErrorMessage(
            'file-upload.file-type-error-server',
            this.allowedFileExtensions().join(', ')
          )
        } else if (error.includes('completed')) {
          this.setErrorMessage('file-upload.audit-completed-error')
        } else {
          this.setErrorMessage('file-upload.general-error')
        }
        this.isDispatching = false
      },
    })
  }

  async downloadFile(documentId: string) {
    const url: string = this.constructFileUrl(documentId)
    return this.http.get(url, {
      responseType: 'blob' as 'json',
      headers: this.headers,
    })
  }

  parseBlob(data: any, fileName?: string) {
    const dataType = data.type
    const binaryData = []
    binaryData.push(data)
    const blob = new Blob([data], { type: dataType })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    document.body.appendChild(a)
    a.setAttribute('style', 'display:none')
    a.href = url
    a.download = fileName
    a.click()
    window.URL.revokeObjectURL(url)
    return url
  }

  setErrorMessage(message: string, value?: string) {
    this.translate.get(message, { value }).subscribe((res: string) => {
      this.validationErrors = [...this.validationErrors, res]
    })
  }

  allowedFileExtensions() {
    const allowed =
      environment.allowedFileExtentions[
        taskType(this.selectedAudit.subRequirement)
      ]
    // TODO we should check why we don't use this  
    // const allowed = this.selectedAudit.nodeDocFileExt.toLowerCase().split(';')
    return allowed || environment.allowedFileExtentions.default
  }

  private constructUrl(participantId, selectedGuid, selectedAuditId) {
    return `${
      environment.endpoints.root +
      environment.endpoints.student +
      '/' +
      participantId +
      '/' +
      environment.endpoints.audits
    }/${selectedGuid}/${
      environment.endpoints.auditDocumentsUpload
    }/${selectedAuditId}`
  }

  private constructFileUrl(documentId: string) {
    const { participantId } = this.store.selectSnapshot(ParticipantState)
    const { selectedGuid } = this.store.selectSnapshot(StudentAdmissionsState)
    return `${
      environment.endpoints.root +
      environment.endpoints.student +
      '/' +
      participantId +
      '/' +
      environment.endpoints.audits
    }/${selectedGuid}/${
      environment.endpoints.auditDocuments
    }/${documentId}/download`
  }
}
